import { create } from 'zustand';
import { isSlideLocked } from '../lib/slideLock';
import { persist } from 'zustand/middleware';
import type {
  AudioManifestItem,
  LessonMeta,
  Slide,
} from '../services/lectureApi';
import type { Citation } from '../services/lectureRagApi';

// A single turn in the Ask-panel conversation.
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  // Rich RAG citations (link to source slide + preview); tolerant of legacy
  // bare-string chunk ids from older persisted chats / responses.
  citations?: Array<Citation | string>;
  // Slide the question was asked from (for context display).
  slideIndex?: number;
  timestamp: number;
}

// Which side panel is open. Only one at a time keeps the compact layout clean.
export type PanelMode = 'none' | 'explain' | 'ask' | 'tutor' | 'class';

interface LectureState {
  // ---- Loaded lesson content (not persisted; re-fetched per visit) ----------
  currentLessonId: string | null;
  lesson: LessonMeta | null;
  slides: Slide[];
  // slide_id -> audio manifest entry (public_url, duration_ms).
  audioBySlideId: Record<string, AudioManifestItem>;
  isLoaded: boolean;
  loadError: string | null;

  // ---- Transport / playback (live, not persisted) --------------------------
  currentSlideIndex: number;
  isPlaying: boolean;
  audioPosition: number; // seconds into the current slide's audio
  playbackRate: number;

  // ---- Progress (persisted slice) ------------------------------------------
  completedSlides: string[]; // slide ids whose audio finished / widget done
  // Admins/super-admins bypass advance-when-covered gating. Held here rather
  // than read from the auth store inside an action, because the gate now runs
  // in the store's navigation actions and a zustand action must not subscribe
  // to another store. Set by the lecture page when it loads a lesson.
  isAdmin: boolean;
  xp: number;

  // ---- Side panels + chat (chat persisted per lesson) ----------------------
  panel: PanelMode;
  chatHistory: ChatMessage[];

  // ---- Presenter: Notes (narration transcript) visibility ------------------
  // A single presenter-controlled switch. When OFF the transcript section is
  // never rendered anywhere (students watching a presentation must not see it);
  // when ON an admin/instructor can open and read the narration. Persisted so
  // the choice sticks across slides and sessions. Default OFF.
  notesVisible: boolean;

  // ---- Actions -------------------------------------------------------------
  loadLesson: (
    lessonId: string,
    lesson: LessonMeta,
    slides: Slide[],
    audio: AudioManifestItem[]
  ) => void;
  setLoadError: (msg: string | null) => void;
  resetForLesson: (lessonId: string) => void;

  goToSlide: (index: number) => void;
  nextSlide: () => void;
  prevSlide: () => void;
  setIsAdmin: (v: boolean) => void;
  /** Is `index` locked for this learner right now? Same rule the rail draws. */
  isSlideLocked: (index: number) => boolean;

  setPlaying: (playing: boolean) => void;
  setPosition: (seconds: number) => void;
  setPlaybackRate: (rate: number) => void;

  markSlideCovered: (slideId: string) => void;
  markComplete: (slideId: string) => void;

  setPanel: (mode: PanelMode) => void;
  addChatMessage: (msg: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  clearChat: () => void;

  setNotesVisible: (visible: boolean) => void;

  // ---- Derived helpers -----------------------------------------------------
  currentSlide: () => Slide | undefined;
  audioForSlide: (index: number) => AudioManifestItem | undefined;
  isSlideCovered: (index: number) => boolean;
}

const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];
// Fine speed-slider bounds + 5% step.
const RATE_MIN = 0.5;
const RATE_MAX = 2;
const RATE_STEP = 0.05;

function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.max(0, Math.min(index, length - 1));
}

export const useLectureStore = create<LectureState>()(
  persist(
    (set, get) => ({
      currentLessonId: null,
      lesson: null,
      slides: [],
      audioBySlideId: {},
      isLoaded: false,
      loadError: null,

      currentSlideIndex: 0,
      isPlaying: false,
      audioPosition: 0,
      playbackRate: 1,

      completedSlides: [],
      isAdmin: false,
      xp: 0,

      panel: 'none',
      chatHistory: [],
      notesVisible: false,

      loadLesson: (lessonId, lesson, slides, audio) => {
        const audioBySlideId: Record<string, AudioManifestItem> = {};
        for (const a of audio) audioBySlideId[a.slide_id] = a;
        set({
          currentLessonId: lessonId,
          lesson,
          slides,
          audioBySlideId,
          isLoaded: true,
          loadError: null,
        });
      },

      setLoadError: (msg) => set({ loadError: msg, isLoaded: false }),

      // Called when we navigate to a different lesson than the persisted one:
      // wipe lesson-scoped state (progress carries per-slide-id so it survives a
      // re-load of the same lesson, but chat + transport reset on a new lesson).
      resetForLesson: (lessonId) => {
        const prev = get().currentLessonId;
        if (prev === lessonId) return;
        set({
          currentLessonId: lessonId,
          lesson: null,
          slides: [],
          audioBySlideId: {},
          isLoaded: false,
          loadError: null,
          currentSlideIndex: 0,
          isPlaying: false,
          audioPosition: 0,
          completedSlides: [],
          xp: 0,
          panel: 'none',
          chatHistory: [],
        });
      },

      setIsAdmin: (v) => set({ isAdmin: !!v }),

      isSlideLocked: (index) => {
        const { slides, completedSlides, currentSlideIndex, isAdmin } = get();
        return isSlideLocked({ slides, completedSlides, currentSlideIndex, isAdmin }, index);
      },

      // Advance-when-covered gating is enforced HERE, not in the components.
      // It used to live only inside SlideProgressRail, so the rail drew a
      // padlock and refused the click while the transport's Next button —
      // which calls this store directly — stepped straight over it. The
      // instructor found it: "lesson in left list are locked ... but clicking
      // on Next and Previous beside play button auto unlocks."
      //
      // A rule that only one of several callers applies is not a rule. Putting
      // it in the action means the rail, the transport and any keyboard
      // shortcut are all gated by construction, and a future control cannot
      // reintroduce the bypass by forgetting to ask.
      goToSlide: (index) => {
        const { slides, completedSlides, currentSlideIndex, isAdmin } = get();
        const target = clampIndex(index, slides.length);
        const ctx = { slides, completedSlides, currentSlideIndex, isAdmin };
        if (isSlideLocked(ctx, target)) return;
        set({ currentSlideIndex: target, audioPosition: 0, isPlaying: false });
      },

      nextSlide: () => {
        const { currentSlideIndex, slides, completedSlides, isAdmin } = get();
        if (currentSlideIndex >= slides.length - 1) return;
        const ctx = { slides, completedSlides, currentSlideIndex, isAdmin };
        if (isSlideLocked(ctx, currentSlideIndex + 1)) return;
        set({
          currentSlideIndex: currentSlideIndex + 1,
          audioPosition: 0,
          isPlaying: false,
        });
      },

      // Deliberately ungated: moving BACK is always allowed. Every earlier
      // slide is open for review by definition of the rule above, and gating
      // backwards would trap a learner who wanted to re-read something.
      prevSlide: () => {
        const { currentSlideIndex } = get();
        if (currentSlideIndex <= 0) return;
        set({
          currentSlideIndex: currentSlideIndex - 1,
          audioPosition: 0,
          isPlaying: false,
        });
      },

      setPlaying: (playing) => set({ isPlaying: playing }),
      setPosition: (seconds) => set({ audioPosition: seconds }),
      setPlaybackRate: (rate) => {
        // Fine-grained: clamp to the supported range and round to 5% steps so the
        // slider and the preset buttons both land on clean values (e.g. 1.25).
        const clamped = Math.max(RATE_MIN, Math.min(RATE_MAX, rate));
        const stepped = Math.round(clamped / RATE_STEP) * RATE_STEP;
        set({ playbackRate: Math.round(stepped * 100) / 100 });
      },

      // "Covered" = audio finished OR widget completed. Used for advance gating
      // and the progress rail without necessarily counting as fully complete.
      markSlideCovered: (slideId) => {
        if (!slideId) return;
        set((s) =>
          s.completedSlides.includes(slideId)
            ? s
            : { completedSlides: [...s.completedSlides, slideId] }
        );
      },

      markComplete: (slideId) => {
        if (!slideId) return;
        set((s) => {
          if (s.completedSlides.includes(slideId)) return s;
          return {
            completedSlides: [...s.completedSlides, slideId],
            xp: s.xp + 5,
          };
        });
      },

      setPanel: (mode) => set({ panel: mode }),

      setNotesVisible: (visible) => set({ notesVisible: visible }),

      addChatMessage: (msg) =>
        set((s) => ({
          chatHistory: [
            ...s.chatHistory,
            { ...msg, id: crypto.randomUUID(), timestamp: Date.now() },
          ],
        })),

      clearChat: () => set({ chatHistory: [] }),

      currentSlide: () => {
        const { slides, currentSlideIndex } = get();
        return slides[currentSlideIndex];
      },

      audioForSlide: (index) => {
        const { slides, audioBySlideId } = get();
        const slide = slides[index];
        return slide ? audioBySlideId[slide.id] : undefined;
      },

      isSlideCovered: (index) => {
        const { slides, completedSlides } = get();
        const slide = slides[index];
        return slide ? completedSlides.includes(slide.id) : false;
      },
    }),
    {
      name: 'acechem-lecture',
      // Persist only progress-relevant fields, scoped by lesson id so resuming
      // a different lesson does not show stale progress/chat. Transport state
      // (isPlaying, position) is intentionally NOT persisted here — the resume
      // position comes authoritatively from the backend /progress on load.
      partialize: (s) => ({
        currentLessonId: s.currentLessonId,
        currentSlideIndex: s.currentSlideIndex,
        completedSlides: s.completedSlides,
        xp: s.xp,
        chatHistory: s.chatHistory,
        playbackRate: s.playbackRate,
        notesVisible: s.notesVisible,
      }),
    }
  )
);

export { PLAYBACK_RATES, RATE_MIN, RATE_MAX, RATE_STEP };
